import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Calendar } from 'primereact/calendar';
import { t } from 'i18next';
import { InputTextarea } from 'primereact/inputtextarea';
import { useContext } from 'react';
import { MarsinetContext } from '../../../../../context/marsinetProvider';
import { getLocalDateTimeInTimeZone } from '../../../../../utils/timezone-utils';

type CloseIncidenceDisclaimerDialogProps = {
  visible: boolean;
  loading?: boolean;
  incidentCode?: string;
  onCancel: () => void;
  onConfirm: (closeData: {
    selectedDate: Date | null;
    comment: string;
  }) => void;
};

export const CloseIncidenceDisclaimerDialog: React.FC<
  CloseIncidenceDisclaimerDialogProps
> = ({ visible, loading = false, incidentCode, onCancel, onConfirm }) => {
  const { selectedTimezone } = useContext(MarsinetContext);
  // El selector usa la hora de la zona activa; el componente padre la convierte a UTC.
  const [selectedDateTime, setSelectedDateTime] = React.useState<Date | null>(
    getLocalDateTimeInTimeZone(new Date(), selectedTimezone)
  );
  const [comment, setComment] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const wasVisible = React.useRef(false);

  React.useEffect(() => {
    if (visible && !wasVisible.current) {
      setSelectedDateTime(
        getLocalDateTimeInTimeZone(new Date(), selectedTimezone)
      );
      setComment('');
      setSubmitted(false);
    }
    wasVisible.current = visible;
  }, [visible, selectedTimezone]);


  const trimmedComment = comment.trim();
  const isCommentInvalid = trimmedComment.length === 0;

  const handleConfirmClick = () => {
    setSubmitted(true);
    if (isCommentInvalid) return;

    // Al hacer clic, enviamos los estados locales hacia el componente padre
    onConfirm({
      selectedDate: selectedDateTime,
      comment: trimmedComment,
    });
  };

  const footer = (
    <div className="flex justify-content-end gap-2 pt-3">
      <Button
        label={t('cancel')}
        icon="pi pi-times"
        severity="secondary"
        outlined
        disabled={loading}
        onClick={onCancel}
      />

      <Button
        label={t('pages.incidents.confirmCloseButton')}
        icon="pi pi-check"
        severity="danger"
        loading={loading}
        disabled={loading}
        onClick={handleConfirmClick}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      modal
      draggable={false}
      resizable={false}
      header={t('pages.incidents.buttonCloseIncidence')}
      footer={footer}
      style={{ width: '38rem', maxWidth: '95vw' }}
      contentStyle={{ paddingTop: '0.5rem' }}
      onHide={() => {
        if (!loading) onCancel();
      }}
    >
      <div className="flex flex-column gap-3">
        <div className="flex align-items-start gap-3 p-3 border-round bg-orange-50">
          <i
            className="pi pi-exclamation-triangle text-orange-500 mt-1"
            style={{ fontSize: '1.4rem' }}
          />

          <div className="flex flex-column gap-2">
            <span className="font-semibold text-900">
              {t('pages.incidents.confirmClose')}
            </span>

            <span className="text-sm text-700 line-height-3">
              {t('pages.incidents.confirmCloseMessage')}
            </span>
          </div>
        </div>

        {incidentCode && (
          <div className="flex flex-column gap-1">
            <span className="text-sm font-medium text-600">
              {t('pages.incidents.titleSingle')}
            </span>

            <span className="font-semibold text-900">{incidentCode}</span>
          </div>
        )}
        <div className="flex flex-column gap-3 p-3 surface-50 border-round">
          <div className="flex flex-column gap-2">
            <span className="text-sm font-medium text-600">
              {t('pages.incidents.dateClosedIncidence')}
            </span>

            <Calendar
              value={selectedDateTime}
              onChange={(e) => setSelectedDateTime(e.value as Date)}
              dateFormat="dd/mm/yy"
              showIcon
              showTime
              hourFormat="24"
              readOnlyInput
            />
          </div>
        </div>

        <div className="flex flex-column gap-2">
          <label htmlFor="close-incidence-comment" className="text-sm font-medium text-600">
            {t('pages.incidents.closeComment')} <span className="text-red-500">*</span>
          </label>

          <InputTextarea
            id="close-incidence-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            autoResize
            maxLength={500}
            disabled={loading}
            className={submitted && isCommentInvalid ? 'p-invalid' : ''}
          />

          {submitted && isCommentInvalid && (
            <small className="p-error">{t('pages.incidents.closeCommentRequired')}</small>
          )}
        </div>
        <Divider className="my-2" />

        <div className="flex flex-column gap-3 text-700 line-height-3">
          <p className="m-0">{t('pages.incidents.autoCloseInfo')}</p>
        </div>

        <div className="p-3 border-round surface-100">
          <p className="m-0 font-semibold text-900">
            {t('pages.incidents.doubleConfirmClose')}
          </p>
        </div>
      </div>
    </Dialog>
  );
};